import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Header from "../components/feature/Header";
import Footer from "../components/feature/Footer";
import WhatsAppFloating from "../components/feature/WhatsAppFloating";
import CookieBanner from "../components/feature/CookieBanner";
import SEO from "../components/feature/SEO";
import { useState } from "react";

const suggestedPages = [
  {
    title: "Persianas en Málaga",
    desc: "Reparación e instalación de persianas en toda la Costa del Sol.",
    to: "/persianas/",
    icon: "ri-window-line",
    color: "primary",
  },
  {
    title: "Toldos motorizados",
    desc: "Toldos con motor Somfy, cofre y extensibles a medida.",
    to: "/toldos/",
    icon: "ri-sun-line",
    color: "secondary",
  },
  {
    title: "Mosquiteras",
    desc: "Enrollables, correderas y fijas desde 60€.",
    to: "/mosquiteras/",
    icon: "ri-bug-line",
    color: "accent",
  },
];

export default function NotFound() {
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Página no encontrada | Persianas Toldos Málaga"
        description="La página que buscas no existe. Encuentra persianas, toldos y mosquiteras en Málaga."
        ogImage="https://persianas-toldos-malaga.es/og/og-default.jpg"
        ogType="website"
        robots="noindex, nofollow"
      />
      <Header />
      <main className="pt-24 md:pt-[72px]">
        {/* HERO CENTRADO */}
        <section className="bg-surface-alt py-16 md:py-28">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-7xl md:text-9xl font-heading font-extrabold text-primary/15 mb-2" aria-hidden="true">
                404
              </div>
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-text-primary mb-4">
                Ups... página no encontrada
              </h1>
              <p className="font-body text-lg text-text-secondary max-w-md mx-auto mb-10">
                La página que buscas no existe o ha sido movida.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-base px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">🏠</span> Volver al inicio
                </Link>
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-2 bg-white border-2 border-border hover:border-primary text-text-primary hover:text-primary font-body font-bold text-base px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">📞</span> Llámanos: 951 12 07 70
                </a>
              </div>

              <a
                href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-whatsapp hover:text-[#128C7E] font-body font-semibold text-base transition-colors"
              >
                <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i> Hablar por WhatsApp
              </a>
            </motion.div>
          </div>
        </section>

        {/* BUSCADOR */}
        <section className="py-10 md:py-14 border-b border-border">
          <div className="max-w-xl mx-auto px-4 md:px-6">
            <label htmlFor="search-404" className="sr-only">
              Buscar en el sitio
            </label>
            <div className="relative">
              <i
                className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
                aria-hidden="true"
              ></i>
              <input
                id="search-404"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar en el sitio..."
                className="w-full h-14 pl-12 pr-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>
            {searchQuery.length > 0 && (
              <div className="mt-4 bg-white border border-border rounded-xl p-4 shadow-sm">
                <p className="font-body text-sm text-text-secondary mb-2">Sugerencias:</p>
                <ul className="space-y-2">
                  {suggestedPages
                    .filter(
                      (p) =>
                        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        p.desc.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((p) => (
                      <li key={p.to}>
                        <Link
                          to={p.to}
                          className="flex items-center gap-3 font-body text-sm text-text-primary hover:text-primary transition-colors py-1"
                        >
                          <i className={`${p.icon} text-lg`} aria-hidden="true"></i>
                          {p.title}
                        </Link>
                      </li>
                    ))}
                  {suggestedPages.filter(
                    (p) =>
                      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.desc.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0 && (
                    <li className="font-body text-sm text-text-secondary">
                      No encontramos resultados. Prueba con "persianas", "toldos" o "mosquiteras".
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* SUGERENCIAS */}
        <section className="py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-text-primary text-center mb-10">
              Quizás te interese
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {suggestedPages.map((page, i) => (
                <motion.div
                  key={page.to}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                >
                  <Link
                    to={page.to}
                    className="flex flex-col items-center gap-4 p-6 md:p-8 bg-white border border-border rounded-2xl hover:border-primary transition-colors text-center h-full"
                  >
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${
                        page.color === "primary"
                          ? "bg-primary/10 text-primary"
                          : page.color === "secondary"
                          ? "bg-secondary/10 text-secondary"
                          : "bg-accent/10 text-accent"
                      }`}
                    >
                      <i className={`${page.icon} text-2xl`} aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg text-text-primary mb-1">
                        {page.title}
                      </h3>
                      <p className="font-body text-sm text-text-secondary">{page.desc}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}